import {
  Box,
  Flex,
  Button,
  useDisclosure,
  Tooltip,
  HStack,
} from '@chakra-ui/react';
import React from 'react';

import { useCreateInvoiceContext } from '../../hooks/useCreateInvoice';
import {
  CreateInvoiceHeaderWithTimelineStepper,
  CreateInvoiceStepIndex,
} from './CreateInvoiceHeaderWithTimelineStepper';
import { CreateInvoiceLineItems } from './CreateInvoiceLineItems';
import { colors } from 'packages/common-frontend/colors';
import { IconButtonV2 } from 'packages/common-frontend/components/IconButton';
import { Icon } from 'packages/common-frontend/components/icons';
import { Typography } from 'packages/common-frontend/components/Typography';
import { CreateInvoiceAddComponentSideDrawer } from 'packages/invoices/ui/create/CreateInvoiceAddComponentSideDrawer';
import { CreateInvoiceAddFacilityFeeComponentButton } from 'packages/invoices/ui/create/CreateInvoiceAddFacilityFeeComponentButton';
import { CreateInvoiceFacilityFeeInvoiceComponent } from 'packages/invoices/ui/create/CreateInvoiceFacilityFeeInvoiceComponent';

export const CreateMultiInvoiceComponents = ({
  setCurrentStepIndex,
}: {
  setCurrentStepIndex: React.Dispatch<
    React.SetStateAction<CreateInvoiceStepIndex>
  >;
}) => {
  const {
    isOpen: isOpenAddComponentSideDrawer,
    onOpen: onOpenAddComponentSideDrawer,
    onClose: onCloseAddComponentSideDrawer,
  } = useDisclosure();

  const {
    invoiceComponentsWithInvoiceItems,
    selectedEntities: {
      creditFacility: { creditFacilityLoans },
    },
    deleteInvoiceComponent,
  } = useCreateInvoiceContext();

  const hasInvoiceComponents = invoiceComponentsWithInvoiceItems.length > 0;

  const everyComponentHasInvoiceItemsAndAmount = invoiceComponentsWithInvoiceItems.every(
    (invoiceComponent) => {
      const hasInvoiceItems = invoiceComponent.invoiceItems.length > 0;
      const everyInvoiceItemHasAmount = invoiceComponent.invoiceItems.every(
        (invoiceItem) => invoiceItem.amount > 0,
      );
      return hasInvoiceItems && everyInvoiceItemHasAmount;
    },
  );

  const canClickContinue =
    hasInvoiceComponents && everyComponentHasInvoiceItemsAndAmount;

  if (invoiceComponentsWithInvoiceItems.length === 0) {
    return (
      <Flex direction="column" h="100vh">
        <Box mb="40px">
          <CreateInvoiceHeaderWithTimelineStepper completedStepIndex={2} />
        </Box>

        <Flex
          direction="column"
          flexGrow={1}
          m="40px"
          textAlign="center"
          alignItems="center"
        >
          <Typography variant="section-title">
            Add items to your invoice
          </Typography>
          <Typography variant="base-muted">
            Add loans or facility fees to continue.
          </Typography>
          <HStack gap="32px">
            <Button
              variant="secondary"
              colorScheme="v2-aurora"
              onClick={onOpenAddComponentSideDrawer}
              leftIcon={<Icon icon="plus" />}
              mt="40px"
              w="fit-content"
            >
              Add Loans
            </Button>
            <Box mt="40px">
              <CreateInvoiceAddFacilityFeeComponentButton />
            </Box>
          </HStack>
        </Flex>

        <CreateInvoiceAddComponentSideDrawer
          isOpen={isOpenAddComponentSideDrawer}
          onClose={onCloseAddComponentSideDrawer}
        />
        {/* Form Navigation Footer */}
        <Flex justifyContent="space-between" py="40px">
          <Button variant="v2-ghost" onClick={() => setCurrentStepIndex(1)}>
            Back
          </Button>
          <Button
            variant="primary"
            colorScheme="v2-aurora"
            onClick={() => setCurrentStepIndex(3)}
            isDisabled
          >
            Continue
          </Button>
        </Flex>
      </Flex>
    );
  }

  return (
    <>
      <Box mb="40px">
        <CreateInvoiceHeaderWithTimelineStepper completedStepIndex={2} />
        <Flex justifyContent="flex-end" mt="40px">
          <HStack gap="32px">
            <Button
              variant="secondary"
              colorScheme="v2-aurora"
              leftIcon={<Icon icon="plus" />}
              onClick={onOpenAddComponentSideDrawer}
            >
              Add Loan
            </Button>
            {invoiceComponentsWithInvoiceItems.every(
              (invoiceComponent) =>
                invoiceComponent.invoiceComponent.invoiceComponentType !==
                'creditFacility',
            ) && <CreateInvoiceAddFacilityFeeComponentButton />}
          </HStack>
        </Flex>
        <CreateInvoiceAddComponentSideDrawer
          isOpen={isOpenAddComponentSideDrawer}
          onClose={onCloseAddComponentSideDrawer}
        />
      </Box>
      {invoiceComponentsWithInvoiceItems.map((invoiceComponent, idx) => {
        const key = idx;

        const loan = creditFacilityLoans?.find(
          (creditFacilityLoan) =>
            creditFacilityLoan.id ===
            invoiceComponent.invoiceComponent.finleyLoanId,
        );

        // Loans invoice component
        if (invoiceComponent.invoiceComponent.invoiceComponentType === 'loan') {
          return (
            <Box key={key} mb="40px">
              <Flex justifyContent="space-between">
                <Typography variant="section-title">
                  {loan?.name ?? '-'}
                </Typography>
                <IconButtonV2
                  icon={<Icon icon="trash-can" />}
                  onClick={() =>
                    deleteInvoiceComponent({ invoiceComponentIndex: idx })
                  }
                  variant="v2-ghost"
                  aria-label="Delete from invoice"
                  label="Delete from invoice"
                />
              </Flex>
              <Box
                borderRadius="8px"
                border="1px"
                borderColor={colors.gray[200]}
                p="40px"
              >
                <CreateInvoiceLineItems invoiceComponentIndex={idx} />
              </Box>
            </Box>
          );
        }

        // Facility fees invoice component
        return (
          <Box mb="40px" key={key}>
            <Flex justifyContent="space-between">
              <Typography variant="section-title">Facility</Typography>
              <IconButtonV2
                icon={<Icon icon="trash-can" />}
                onClick={() =>
                  deleteInvoiceComponent({ invoiceComponentIndex: idx })
                }
                variant="v2-ghost"
                aria-label="Delete from invoice"
                label="Delete from invoice"
              />
            </Flex>
            <CreateInvoiceFacilityFeeInvoiceComponent
              invoiceComponentIndex={idx}
            />
          </Box>
        );
      })}
      {/* Form Navigation Footer */}
      <Flex justifyContent="space-between" mt="40px" pb="40px">
        <Button variant="v2-ghost" onClick={() => setCurrentStepIndex(1)}>
          Back
        </Button>
        <Tooltip
          label={canClickContinue ? '' : 'Every line item must have an amount'}
        >
          <Button
            variant="primary"
            colorScheme="v2-aurora"
            onClick={() => setCurrentStepIndex(3)}
            isDisabled={!canClickContinue}
          >
            Continue
          </Button>
        </Tooltip>
      </Flex>
    </>
  );
};
